import React, { useState, useContext, useEffect } from 'react'
import './styles.scss'

import AUTH_ACTIONS from '../../../../../store/actions/authAction'

import ContractContext from '../../../../../store/contexts/contractContext'

import Button from '../../../../../components/Button'
import GeneralInput from '../../../../../components/GeneralInput'
import GeneralInputMask from '../../../../../components/GeneralInputMask'

import { filterString } from '../../../../../helpers/cleanup'

import { User } from 'types/user'
import ErrorComponent from 'components/ErrorComponent'
import { get } from 'lodash'

type FormObject = {
  label: string
  value: any
  error: string
}

type SignupForm = {
  name: FormObject
  cpf: FormObject
  email: FormObject
  phone: FormObject
  password: FormObject
  confirmPassword: FormObject
}

type LoginForm = {
  email: FormObject
  password: FormObject
}

export default function Auth({ urlData }) {
  const [isRegistering, toggleRegister] = useState(!!urlData)

  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: { label: 'E-mail', value: '', error: '' },
    password: { label: 'Senha', value: '', error: '' },
  })

  const [signupForm, setSignupForm] = useState<SignupForm>({
    name: { label: 'Nome', value: urlData?.name ?? '', error: '' },
    cpf: { label: 'CPF', value: '', error: '' },
    email: { label: 'E-mail', value: '', error: '' },
    phone: { label: 'Telefone', value: urlData?.phone ?? '', error: '' },
    password: { label: 'Senha', value: '', error: '' },
    confirmPassword: { label: 'Confirmação de senha', value: '', error: '' },
  })

  const [error, setError] = useState<string[]>([])
  const [userRegistered, setUserRegistered] = useState(false)

  const onChangeLoginState = (value: string, editedItem: string) => {
    //@ts-ignore
    setLoginForm((prevState) => ({ ...prevState, [editedItem]: { ...prevState.editedItem, value } }))
    if (loginForm[editedItem].error) {
      setLoginFormError(editedItem, '')
    }
  }

  const onChangeSignupForm = (value: string, editedItem: string) => {
    //@ts-ignore
    setSignupForm((prevState) => ({ ...prevState, [editedItem]: { ...prevState.editedItem, value } }))
    if (signupForm[editedItem].error) {
      setSignupFormError(editedItem, '')
    }
  }

  const setLoginFormError = (key: string, error: string) => {
    setLoginForm((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        error,
      },
    }))
  }

  const setSignupFormError = (key: string, error: string) => {
    setSignupForm((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        error,
      },
    }))
  }

  const primaryActionLabel = isRegistering ? 'CADASTRAR' : 'ENTRAR'
  const secondaryActionLabel = isRegistering ? 'Voltar' : 'CADASTRAR'

  const {
    services,
    auth: { authDispatch },
    handleStepChange,
    setIsLoading,
  } = useContext(ContractContext)

  const validateLoginForm = () => {
    let isValid = true
    Object.keys(loginForm).forEach((loginItem) => {
      if (!loginForm[loginItem].value) {
        setLoginFormError(loginItem, `O campo ${loginForm[loginItem].label} é obrigatório`)
        isValid = false
      }
    })
    return isValid
  }

  const logUser = async (loginForm: LoginForm, step = 'CPFRegister') => {
    setError([])
    const { email, password } = loginForm

    const isValid = validateLoginForm()

    if (!isValid) {
      return
    }

    setIsLoading(true)

    const lowerCaseEmail = email.value.toLowerCase()

    try {
      const { user, accessToken }: { user: User; accessToken: string } = await services.authenticateUser(
        lowerCaseEmail,
        password.value,
      )
      authDispatch({
        type: AUTH_ACTIONS.SET_LOGGED_USER_DATA,
        value: { ...user, accessToken },
      })
      localStorage.setItem('current_user', JSON.stringify({ user, accessToken }))
      setIsLoading(false)
      if (!user.phone_confirmed) {
        handleStepChange('CellPhoneValidation')
        return
      }
      console.log('🚀 ~ logUser ~ user', user)

      handleStepChange(user.cpf && user.address.zip_code ? 'TenantRegister' : step)
    } catch (err) {
      setIsLoading(false)
      setError([
        'Ops! Usuário não encontrado. Por favor, revise seu e-mail e senha ou tente novamente mais tarde.',
      ])
      console.log(err.message)
    }
  }

  const validateSignupForm = () => {
    let isValid = true

    Object.keys(signupForm).forEach((field) => {
      if (!Boolean(signupForm[field].value)) {
        setSignupFormError(field, `O campo ${signupForm[field].label} é obrigatório`)
        isValid = false
      }
    })

    if (signupForm.email.value && signupForm.email.value.indexOf('@') === -1) {
      setSignupFormError('email', 'Por favor insira um email válido')
      isValid = false
    }

    if (signupForm.phone.value && filterString(signupForm.phone.value, ['(', ')', '+', '_']).length < 12) {
      setSignupFormError('phone', 'Por favor insira um Telefone válido')
      isValid = false
    }

    if (signupForm.cpf.value && filterString(signupForm.cpf.value, ['(', ')', '+', '_']).length < 11) {
      setSignupFormError('cpf', 'Por favor insira um CPF válido')
      isValid = false
    }

    if (signupForm.password.value !== signupForm.confirmPassword.value) {
      setSignupFormError(
        'confirmPassword',
        'As senhas não são iguais. Por favor verifique novamente as senhas.',
      )
      isValid = false
    }

    return isValid
  }

  const registerUser = async (signupForm: SignupForm) => {
    setError([])
    const { name, email, password, phone, cpf } = signupForm

    const isValid = validateSignupForm()

    if (!isValid) {
      return
    }

    const payload = {
      name: name.value,
      password: password.value,
      email: email.value.toLowerCase(),
      phone: filterString(phone.value, ['(', ')', ' ', '-']),
      cpf: filterString(cpf.value, ['.', '-']),
    }

    try {
      if (urlData) {
        await services.customSignup(payload)
      } else {
        await services.registerNewUser(payload)
      }
      setLoginForm({ email, password })
      setUserRegistered(true)
      setError([''])
    } catch (err) {
      console.log({ err })
      switch (get(err, 'data.code')) {
        case 'duplicated_email':
          setError(['Email já consta cadastrado no nosso sistema. Por favor insira outro email'])
          break
        case 'invalid_data':
          if (get(err, 'data.details[0].message') === 'Invalid CPF') {
            setError(['Por favor insira um CPF válido.'])
          } else {
            setError(['Não foi possível fazer o registro no momento. Por favor tente novamente mais tarde.'])
          }
          break
        default:
          setError(['Não foi possível fazer o registro no momento. Por favor tente novamente mais tarde.'])
      }
    }
  }

  const handleAuth = () => {
    if (isRegistering) {
      registerUser(signupForm)
      return
    }

    logUser(loginForm)
  }

  const checkLocalStorage = () => {
    setIsLoading(true)
    const currentUser = localStorage.getItem('current_user')
    if (currentUser) {
      const { user, accessToken }: { user: User; accessToken: string } = JSON.parse(currentUser)
      authDispatch({
        type: AUTH_ACTIONS.SET_LOGGED_USER_DATA,
        value: { ...user, accessToken },
      })

      if (!user.phone_confirmed) {
        handleStepChange('CellPhoneValidation')
      } else {
        handleStepChange(user.cpf && user.address.zip_code ? 'TenantRegister' : 'CPFRegister')
      }
    }
    setIsLoading(false)
  }

  const logInRegisteredUser = async () => {
    await logUser(loginForm, 'CellPhoneValidation')
  }

  useEffect(() => {
    checkLocalStorage()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (userRegistered && loginForm.email.value && loginForm.password.value) {
      logInRegisteredUser()
    }
  }, [userRegistered, loginForm]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className='auth-container'>
      <div className='errors-container'>
        <ErrorComponent errors={error} />
      </div>
      {isRegistering ? (
        <div>
          <p>Seu nome e dados serão considerados como informações do dono do imóvel</p>
          <div>
            <GeneralInput
              label='Nome'
              handleChange={(e) => onChangeSignupForm(e.target.value, 'name')}
              value={signupForm.name.value}
              error={signupForm.name.error}
            />
            <GeneralInputMask
              label='CPF'
              mask={[/\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '.', /\d/, /\d/, /\d/, '-', /\d/, /\d/]}
              handleChange={(e) => onChangeSignupForm(e.target.value, 'cpf')}
              error={signupForm.cpf.error}
            />
            <GeneralInput
              label='E-mail'
              handleChange={(e) => onChangeSignupForm(get(e, 'target.value', '').toLowerCase(), 'email')}
              error={signupForm.email.error}
            />
            <GeneralInputMask
              label='Telefone'
              // @ts-ignore
              mask={['(', /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
              handleChange={(e) => onChangeSignupForm(e.target.value, 'phone')}
              value={signupForm.phone.value}
              error={signupForm.phone.error}
            />
            <GeneralInput
              label='Senha'
              type='password'
              handleChange={(e) => onChangeSignupForm(e.target.value, 'password')}
              error={signupForm.password.error}
            />
            <GeneralInput
              label='Confirmar Senha'
              type='password'
              handleChange={(e) => onChangeSignupForm(e.target.value, 'confirmPassword')}
              error={signupForm.confirmPassword.error}
            />
          </div>
        </div>
      ) : (
        <>
          <p className='subtitle'>Seu nome e dados serão considerados como informações do dono do imóvel</p>
          <GeneralInput
            label='E-mail'
            value={loginForm.email.value}
            handleChange={(e) => onChangeLoginState(get(e, 'target.value', '').toLowerCase(), 'email')}
            error={loginForm.email.error}
          />
          <GeneralInput
            label='Senha'
            type='password'
            handleChange={(e) => onChangeLoginState(e.target.value, 'password')}
            error={loginForm.password.error}
          />
        </>
      )}
      <div className='action-buttons'>
        <Button
          title={secondaryActionLabel}
          onClick={() => {
            toggleRegister(!isRegistering)
          }}
        />
        <Button title={primaryActionLabel} onClick={handleAuth} />
      </div>
      {isRegistering && (
        <div>
          <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: 16 }}>
            Ao cadastrar na Mellro você concorda com os
          </p>
          {/* TODO: add link - ask osni */}
          <a
            href='https://mellro.com/politica-de-privacidade/'
            target='_blank'
            rel='noopener noreferrer'
            style={{ textAlign: 'center', fontSize: 16, display: 'block' }}
          >
            Política de privacidade
          </a>
        </div>
      )}
    </div>
  )
}
